/** Geometry the kinds share: turning an outline into surfaces, and lens/mirror curves. */
import { Part, Surface, Vec2, World } from './types';
import { toWorld } from './vec';

/**
 * How far a spherical face of radius R has curved away from its vertex at
 * height y. Positive R curves towards +x. Drawing only: the physics of a lens
 * or mirror is one plane (engine target §3). Heights past the sphere are clamped
 * so an aperture wider than 2|R| still draws.
 */
export function sag(R: number | null, y: number): number {
    if (R === null || !Number.isFinite(R)) return 0;
    const yy = Math.min(Math.abs(y), Math.abs(R) * 0.999);
    return R - Math.sign(R) * Math.sqrt(R * R - yy * yy);
}

/** Heights from −h to h, for drawing a curved face. */
export const heights = (h: number, n = 24): number[] =>
    Array.from({ length: n + 1 }, (_, i) => -h + (2 * h * i) / n);

export const localToWorld = (part: Part, pts: Vec2[]): Vec2[] =>
    pts.map(p => toWorld(part.at, part.angle, p));

/**
 * A solid of refractive index `n` from its outline, ANTICLOCKWISE in the
 * part's own frame so every face's outward normal points out. Rotation keeps
 * the winding, which is why the outline is given before it is placed.
 */
export function solidSurfaces(
    part: Part, w: World, local: Vec2[], n: number, faceNames: string[], partName: string, dispersion?: number,
): Surface[] {
    const pts = localToWorld(part, local);
    return pts.map((a, i) => ({
        id: `${part.id}:${i}`,
        partId: part.id,
        shape: { form: 'segment' as const, a, b: pts[(i + 1) % pts.length] },
        rule: { law: 'refract' as const, outside: w.medium, inside: n, ...(dispersion ? { dispersion } : {}) },
        name: `${faceNames[i] ?? `face ${i + 1}`} of ${partName}`,
    }));
}

/** A thin rectangle in the part's frame, centred on x = cx, for drawing housings. */
export const box = (cx: number, halfW: number, halfH: number, cy = 0): Vec2[] => [
    { x: cx - halfW, y: cy - halfH }, { x: cx + halfW, y: cy - halfH },
    { x: cx + halfW, y: cy + halfH }, { x: cx - halfW, y: cy + halfH },
];

/** A regular polygon standing in for a circle, in the part's frame. */
export const disc = (r: number, n = 20): Vec2[] =>
    Array.from({ length: n }, (_, i) => ({ x: r * Math.cos((2 * Math.PI * i) / n), y: r * Math.sin((2 * Math.PI * i) / n) }));
